'use client'
import CheapestQuickestNewBadge from '@/src/components/common/srp/cheapestQuickestCardLabel/cheapestQuickestNew'
import YellowBannerPromo from '@/src/components/common/srp/yellowBannerPromo'
import { FlightCard } from '@/src/components/desktop/srp/flightCard'
import { useGetCheapestQuickestCard } from '@/src/hooks/srp/getCheapestQuickestCard'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { FlightCard as FlightCardType } from '@/src/types/flight'
import { TranslationFunction } from '@/src/types/translations'
import { extractFlightCardProps, GetFlightDataReturn } from '@/src/utils/srp/flightResultsUtil'
import { cardGrouping } from '@/src/utils/srp/searchUtils/cardGrouping'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Button, Card, Collapse, Typography } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import ReactList from 'react-list'
import DateCarouselWrapper from '../dateCarousel/DateCarouselWrapper'

// Static style objects to avoid recreating them on every render
const containerBoxSx = { p: 2, border: '1px solid #e0e0e0', backgroundColor: '#fff', borderRadius: 1.5 }
const headerBoxSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 1,
    borderColor: 'grey.200'
}
const routeTypographySx = { fontWeight: 600, color: 'grey.700' }
const cardWrapperSx = { pb: 1 }
const cardSx = { borderRadius: 1, border: '1px solid #e0e0e0', overflow: 'hidden', boxShadow: 'none', p: 0, mx: 0 }
const mainFlightBoxSx = { px: 3, pt: 1.5, pb: 1.5 }
const expandedListContainerSx = { bgcolor: '#f5f8fa', pt: 1.5, pb: 1.5 }
const expandedItemBoxSx = { mx: 1.5, my: 1.5, p: 1.5, bgcolor: 'white', borderRadius: 1 }
const toggleMoreBoxBaseSx = {
    cursor: 'pointer',
    px: 3,
    background: '#f7f7f7',
    borderTop: '1px solid #eee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    fontWeight: 500,
    color: 'primary.main',
    fontSize: '0.90rem',
    height: 35,
    '&:hover': { background: '#f0f4ff' }
}
const innerListContainerSx = { px: 2 }

interface FlightResultsProps {
    sortedFilteredCards: FlightCardType[]
    getFlightData: (flight: FlightCardType) => GetFlightDataReturn
    route?: string
    onBookFlight?: (flight: FlightCardType) => void
    onViewDetails?: (flight: FlightCardType) => void
    onSortByPrice?: () => void
    // selectedDate: string
    // onDateChange: (date: string) => void
    sectors: any
    tCommon: TranslationFunction<'common'>
    tFlightDetails: TranslationFunction<'srpFlightDetails'>
    isBookingInProgress?: boolean
    loadingFlightKey?: string | null
    limitedView?: boolean
    limitedCount?: number
    showLoadMore?: boolean
    onLoadMore?: () => void
}

const FlightResults: React.FC<FlightResultsProps> = ({
    sortedFilteredCards = [],
    getFlightData,
    route,
    onBookFlight,
    onViewDetails,
    onSortByPrice,
    // selectedDate,
    // onDateChange,
    sectors,
    tCommon,
    tFlightDetails,
    isBookingInProgress,
    loadingFlightKey,
    limitedView = false,
    limitedCount = 5,
    showLoadMore = false,
    onLoadMore
}) => {
    // Memoize grouping to avoid recomputation on unrelated re-renders
    const groupEntries = useMemo(() => cardGrouping(sortedFilteredCards, false), [sortedFilteredCards])
    const { isPerPaxPricingEnabled } = useSearchResults()

    // Handle limited view for flight schedule page
    const displayGroupEntries = useMemo(() => {
        if (limitedView) {
            return groupEntries.slice(0, limitedCount)
        }
        return groupEntries
    }, [groupEntries, limitedView, limitedCount])

    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

    const handleExpand = useCallback((groupKey: string) => {
        setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))
    }, [])

    const renderExpandedSamePriceCards = useCallback(
        (groupKey: string, cards: FlightCardType[]) => (
            <>
                <Box sx={expandedListContainerSx}>
                    <ReactList
                        length={cards.length}
                        itemRenderer={(idx, key) => {
                            const flight = cards[idx]
                            return (
                                <Box key={key} sx={expandedItemBoxSx}>
                                    <FlightCard
                                        {...extractFlightCardProps(
                                            flight,
                                            sectors,
                                            getFlightData,
                                            isPerPaxPricingEnabled
                                        )}
                                        onBook={() => onBookFlight?.(flight)}
                                        onViewDetails={() => onViewDetails?.(flight)}
                                        tCommon={tCommon}
                                        tFlightDetails={tFlightDetails}
                                        isBookingLoading={loadingFlightKey === flight.priceBreakup.fare.fk}
                                        isDisabled={
                                            isBookingInProgress && loadingFlightKey !== flight.priceBreakup.fare.fk
                                        }
                                    />
                                </Box>
                            )
                        }}
                    />
                </Box>
                <Box onClick={() => handleExpand(groupKey)} sx={toggleMoreBoxBaseSx}>
                    {tFlightDetails('hideMoreOption')}
                </Box>
            </>
        ),
        [
            getFlightData,
            handleExpand,
            isBookingInProgress,
            loadingFlightKey,
            onBookFlight,
            onViewDetails,
            sectors,
            tCommon,
            tFlightDetails
        ]
    )

    const quickestCheapestCard = useGetCheapestQuickestCard()

    const renderMainCard = useCallback(
        (index: number, key: React.Key) => {
            const [groupKey, cards] = displayGroupEntries[index]
            const isExpanded = expandedGroups[groupKey]
            const mainCard = cards[0]
            const extraCards = cards.length > 1 ? cards.slice(1) : []

            if (quickestCheapestCard?.some(sortOption => sortOption?.card?.id === mainCard.id)) {
                return null
            }

            return (
                <Box key={key} sx={cardWrapperSx}>
                    <Card sx={{ ...cardSx, position: 'relative' }}>
                        <YellowBannerPromo promos={mainCard.promos} />
                        <>
                            <Box sx={{ ...mainFlightBoxSx, pt: mainCard.promos?.length > 0 ? 2.5 : 1.5 }}>
                                <FlightCard
                                    {...extractFlightCardProps(
                                        mainCard,
                                        sectors,
                                        getFlightData,
                                        isPerPaxPricingEnabled
                                    )}
                                    onBook={() => onBookFlight?.(mainCard)}
                                    onViewDetails={() => onViewDetails?.(mainCard)}
                                    tCommon={tCommon}
                                    tFlightDetails={tFlightDetails}
                                    isBookingLoading={loadingFlightKey === mainCard.priceBreakup.fare.fk}
                                    isDisabled={
                                        isBookingInProgress && loadingFlightKey !== mainCard.priceBreakup.fare.fk
                                    }
                                />
                            </Box>
                            {extraCards.length > 0 && !isExpanded && (
                                <Box onClick={() => handleExpand(groupKey)} sx={toggleMoreBoxBaseSx}>
                                    +{extraCards.length} {tFlightDetails('moreOptionAvailiable')}
                                    <KeyboardArrowDownIcon fontSize="small" />
                                </Box>
                            )}
                        </>

                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderExpandedSamePriceCards(groupKey, extraCards)}
                        </Collapse>
                    </Card>
                </Box>
            )
        },
        [
            expandedGroups,
            getFlightData,
            displayGroupEntries,
            handleExpand,
            isBookingInProgress,
            loadingFlightKey,
            onBookFlight,
            onViewDetails,
            sectors,
            tCommon,
            tFlightDetails,
            renderExpandedSamePriceCards,
            quickestCheapestCard
        ]
    )

    const renderCheapestQuickestCard = useCallback(() => {
        return (
            <Box p={2} borderRadius={1} bgcolor={"#F3F3F3"} mb={1} display={"flex"} flexDirection={"column"} gap={1}>
                {quickestCheapestCard.map(sortOptions => (
                    <CheapestQuickestNewBadge
                        key={sortOptions.id}
                        label={sortOptions.label}
                        isActive={!!sortOptions.isActive}
                        sortOptionId={sortOptions.id}
                        isMobile={false}
                        isCombined={sortOptions.isCombined}
                    >
                        <Card sx={{ ...cardSx, position: 'relative' }}>
                            {/* placeholder box render cheapest and quickest */}
                            <Box sx={{
                                width: sortOptions.isCombined ? "200px" : "100px",
                                height: "20px",
                                // border: "1px solid red",
                            }}></Box>

                            <YellowBannerPromo promos={sortOptions?.card?.promos} left={sortOptions.isCombined ? 200 : 100} />
                            <>
                                <Box
                                    sx={{
                                        ...mainFlightBoxSx,
                                        pt: Number(sortOptions?.card?.promos?.length) > 0 ? 2.5 : 1.5
                                    }}
                                >
                                    <FlightCard
                                        {...extractFlightCardProps(
                                            sortOptions?.card!,
                                            sectors,
                                            getFlightData,
                                            isPerPaxPricingEnabled
                                        )}
                                        onBook={() => onBookFlight?.(sortOptions?.card!)}
                                        onViewDetails={() => onViewDetails?.(sortOptions?.card!)}
                                        tCommon={tCommon}
                                        tFlightDetails={tFlightDetails}
                                        isBookingLoading={loadingFlightKey === sortOptions?.card?.priceBreakup.fare.fk}
                                        isDisabled={
                                            isBookingInProgress &&
                                            loadingFlightKey !== sortOptions?.card?.priceBreakup.fare.fk
                                        }
                                    />
                                </Box>
                            </>
                        </Card>
                    </CheapestQuickestNewBadge>
                ))}
            </Box>
        )
    }, [
        getFlightData,
        onBookFlight,
        onViewDetails,
        sectors,
        tCommon,
        tFlightDetails,
        loadingFlightKey,
        isBookingInProgress
    ])

    return (
        <Box sx={containerBoxSx}>
            <Box sx={headerBoxSx}>
                <Typography variant="subtitle2" sx={routeTypographySx}>
                    {route}
                </Typography>
            </Box>
            <DateCarouselWrapper />
            <Box>
                {renderCheapestQuickestCard()}
            </Box>
            <Box sx={innerListContainerSx}>

                <ReactList length={displayGroupEntries.length} itemRenderer={renderMainCard} threshold={300} />
                {limitedView && showLoadMore && groupEntries.length > limitedCount && (
                    <Box
                        p={2}
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        // borderRadius={1.5}
                        sx={{
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)', // Safari support
                            backgroundColor: 'rgba(255, 255, 255, 0.635)',
                            // border: '1px solid #eee',
                            borderTop: '2px solid rgba(255, 255, 255, 0.2)'
                        }}
                    >
                        <Button variant="customVariantPrimary" onClick={onLoadMore}>
                            {tCommon('loadMore')}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export { FlightResults }
export type { FlightResultsProps }
